import type { ChangeEvent, ReactNode, RefObject } from "react";
import Box from "@mui/material/Box";
import Dialog, { type DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

export type CsvImportProgress =
  | boolean
  | {
      /** Shown under the progress bar */
      label?: ReactNode;
      /** 0–100 for determinate progress; omit for indeterminate */
      value?: number;
    };

export type CsvImportDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  actions: ReactNode;
  maxWidth?: DialogProps["maxWidth"];
  /** When true, backdrop/escape close is ignored */
  busy?: boolean;
  /** Progress chrome above domain content (importing / parsing) */
  progress?: CsvImportProgress;
  /**
   * Optional hidden file input. Domains call `fileInputRef.current?.click()`
   * from action buttons (e.g. “Choose CSV file”).
   */
  fileInputRef?: RefObject<HTMLInputElement | null>;
  accept?: string;
  onFileSelected?: (file: File) => void | Promise<void>;
};

function normalizeProgress(progress: CsvImportProgress | undefined): {
  show: boolean;
  label?: ReactNode;
  value?: number;
} {
  if (!progress) return { show: false };
  if (progress === true) return { show: true };
  return { show: true, label: progress.label, value: progress.value };
}

/**
 * Shared CSV import dialog chrome: title, optional progress, content/result slots,
 * actions, and optional hidden file input. Domain adapters own parse/import logic.
 */
export default function CsvImportDialog({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "md",
  busy = false,
  progress,
  fileInputRef,
  accept = ".csv,text/csv",
  onFileSelected,
}: CsvImportDialogProps) {
  const prog = normalizeProgress(progress);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onFileSelected) return;
    await onFileSelected(file);
  };

  return (
    <>
      {fileInputRef && onFileSelected ? (
        <input ref={fileInputRef} type="file" accept={accept} hidden onChange={onFileChange} />
      ) : null}

      <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {prog.show ? (
            <Box sx={{ py: prog.label ? 2 : 1 }}>
              <LinearProgress
                variant={prog.value != null ? "determinate" : "indeterminate"}
                value={prog.value}
              />
              {prog.label ? (
                <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                  {prog.label}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {children}
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    </>
  );
}
